import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteFooter from "../components/SiteFooter";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

export default function Claim() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ password: "", confirm: "", full_name: "", terms: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/claim/${token}`).then(({ data }) => {
      setPreview(data);
      setForm(f => ({ ...f, full_name: data.athlete_full_name || "" }));
    }).catch(e => setError(e.response?.data?.detail || "Invalid or expired claim link"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setError("Passwords don't match.");
    if (!form.terms) return setError("Please accept the Terms of Service and Privacy Policy.");
    setSubmitting(true);
    try {
      await api.post("/auth/claim", {
        token, password: form.password, full_name: form.full_name,
        terms_accepted: true, privacy_accepted: true,
      });
      await refresh();
      navigate("/app/dashboard");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to claim profile");
    } finally { setSubmitting(false); }
  };

  if (loading) return <FullPage><div className="text-center text-ink-muted py-24">Verifying your claim link…</div></FullPage>;
  if (error && !preview) return (
    <FullPage>
      <div className="surface p-10 max-w-md mx-auto text-center" data-testid="claim-error">
        <div className="label-eyebrow text-[#FF3B30] mb-3">Invalid claim link</div>
        <h1 className="font-heading text-3xl uppercase tracking-tighter mb-4">{error}</h1>
        <p className="text-ink-muted text-sm mb-6">If you believe this is a mistake, ask your manager to send a new invite.</p>
        <Link to="/login" className="btn-ghost-line inline-flex items-center gap-2 text-sm">Go to login</Link>
      </div>
    </FullPage>
  );

  return (
    <FullPage>
      <div className="max-w-2xl mx-auto py-12 md:py-20">
        <div className="label-eyebrow text-volt">Your profile is ready</div>
        <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tighter mt-3" data-testid="claim-headline">
          Claim your<br /> PITCHD profile.
        </h1>
        <p className="text-ink-muted mt-6 text-base leading-relaxed">
          <span className="text-white">{preview.agent_display_name}</span> has set up your professional sponsorship profile on PITCHD. Once you claim it, the profile becomes your shared workspace — you set the password, edit your media kit, and approve every brand partnership.
        </p>

        <div className="surface p-4 mt-8 flex gap-3 items-start text-xs" data-testid="claim-portability-note">
          <CheckCircle2 className="w-4 h-4 text-volt mt-0.5 shrink-0" />
          <div className="text-ink-muted">
            <span className="text-white">Your identity is portable.</span> Even if {preview.agent_display_name} no longer represents you in the future, you keep your profile, media kit, deals and partnership history.
          </div>
        </div>

        <form onSubmit={submit} className="surface p-6 md:p-8 mt-8 space-y-5" data-testid="claim-form">
          <Field label="Your display name">
            <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} maxLength={120}
              data-testid="claim-name-input"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
          </Field>
          <Field label="Email">
            <input value={preview.athlete_email} disabled
              className="w-full bg-bg-elev border border-line text-ink-muted px-4 py-3" />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Set password *">
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} minLength={8}
                data-testid="claim-password-input"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
            </Field>
            <Field label="Confirm password *">
              <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} minLength={8}
                data-testid="claim-confirm-input"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
            </Field>
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.checked }))}
              data-testid="claim-terms-checkbox"
              className="mt-1 accent-volt" />
            <span className="text-xs text-ink-muted">I accept the <Link to="/terms" className="text-volt hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-volt hover:underline">Privacy Policy</Link>.</span>
          </label>
          {error && <div className="text-xs text-[#FF3B30]" data-testid="claim-error-msg">{error}</div>}
          <button type="submit" disabled={submitting} className="btn-volt w-full inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="claim-submit-btn">
            {submitting ? "Claiming…" : <>Claim & enter workspace <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </FullPage>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-bg text-white flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line">
        <BrandLogo variant="wordmark" size={28} />
      </div>
      <div className="flex-1 px-6 md:px-12">{children}</div>
      <SiteFooter />
    </div>
  );
}
function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
